import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Settings, 
  Clock, 
  Wrench, 
  RefreshCw,
  Shield,
  Timer
} from 'lucide-react';

const MaintenancePage = () => {
  const { api, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);

  // Fetch maintenance status
  const fetchStatus = async () => {
    try {
      const res = await api.get('/maintenance/status');
      setStatus(res.data);
      
      // If maintenance is not enabled, redirect to login or dashboard
      if (!res.data.enabled) {
        if (isAuthenticated) {
          navigate('/dashboard');
        } else {
          navigate('/login');
        }
      }
    } catch (err) {
      console.error('Failed to fetch maintenance status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Poll every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Countdown timer
  useEffect(() => {
    if (!status?.remaining_seconds) {
      setCountdown(null);
      return;
    }

    let remaining = status.remaining_seconds;
    
    const updateCountdown = () => {
      if (remaining <= 0) {
        // Maintenance ended - refresh status
        fetchStatus();
        return;
      }
      
      const hours = Math.floor(remaining / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;
      
      setCountdown({
        hours: hours.toString().padStart(2, '0'),
        minutes: minutes.toString().padStart(2, '0'),
        seconds: seconds.toString().padStart(2, '0'),
        total: remaining
      });
      
      remaining--;
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(timer);
  }, [status?.remaining_seconds]);

  // If user is admin, allow them to bypass
  useEffect(() => {
    if (user?.role === 'admin' && status?.enabled) {
      // Admin can still access - show bypass option
    }
  }, [user, status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      <div className="relative z-10 max-w-lg w-full">
        {/* Main Card */}
        <Card className="bg-white/10 backdrop-blur-xl border-white/20 shadow-2xl">
          <CardContent className="p-8 text-center">
            {/* Animated Icon */}
            <div className="relative mx-auto w-24 h-24 mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full animate-ping opacity-30" />
              <div className="absolute inset-2 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                <Wrench className="h-10 w-10 text-white animate-bounce" style={{ animationDuration: '2s' }} />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-white mb-2">
              Under Maintenance
            </h1>
            <p className="text-lg text-purple-200 mb-6">
              {status?.message || "We're performing scheduled maintenance to improve your experience."}
            </p>

            {/* Countdown Timer */}
            {countdown && (
              <div className="mb-8">
                <Badge className="bg-purple-500/30 text-purple-200 border-purple-400/30 mb-4">
                  <Timer className="h-3 w-3 mr-1" />
                  Estimated Time Remaining
                </Badge>
                
                <div className="flex items-center justify-center gap-3">
                  {/* Hours */}
                  <div className="flex flex-col items-center">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 min-w-[70px]">
                      <span className="text-4xl font-bold text-white font-mono">
                        {countdown.hours}
                      </span>
                    </div>
                    <span className="text-xs text-purple-300 mt-2">Hours</span>
                  </div>

                  <span className="text-3xl font-bold text-purple-400">:</span>

                  {/* Minutes */}
                  <div className="flex flex-col items-center">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 min-w-[70px]">
                      <span className="text-4xl font-bold text-white font-mono">
                        {countdown.minutes}
                      </span>
                    </div>
                    <span className="text-xs text-purple-300 mt-2">Minutes</span>
                  </div>

                  <span className="text-3xl font-bold text-purple-400">:</span>

                  {/* Seconds */}
                  <div className="flex flex-col items-center">
                    <div className="bg-white/10 backdrop-blur rounded-xl p-4 min-w-[70px]">
                      <span className="text-4xl font-bold text-white font-mono">
                        {countdown.seconds}
                      </span>
                    </div>
                    <span className="text-xs text-purple-300 mt-2">Seconds</span>
                  </div>
                </div>
              </div>
            )}

            {/* No timer - indefinite maintenance */}
            {!countdown && status?.enabled && (
              <div className="mb-8 p-4 bg-white/5 rounded-xl">
                <Clock className="h-8 w-8 text-purple-300 mx-auto mb-2" />
                <p className="text-purple-200 text-sm">
                  Maintenance will be completed shortly. Please check back later.
                </p>
              </div>
            )}

            {/* Started by info */}
            {status?.started_by && (
              <p className="text-sm text-purple-300/70 mb-6">
                Started by <span className="font-medium">{status.started_by}</span>
              </p>
            )}

            {/* Refresh Button */}
            <Button
              onClick={fetchStatus}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Check Status
            </Button>

            {/* Admin bypass */}
            {user?.role === 'admin' && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <Badge className="bg-emerald-500/30 text-emerald-200 border-emerald-400/30 mb-3">
                  <Shield className="h-3 w-3 mr-1" />
                  Admin Access
                </Badge>
                <p className="text-sm text-purple-300 mb-3">
                  As an admin, you can still access the platform.
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    onClick={() => navigate('/dashboard')}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Go to Dashboard
                  </Button>
                  <Button
                    onClick={() => navigate('/settings')}
                    variant="outline"
                    className="border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/20"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-purple-300/50 text-sm mt-6">
          We apologize for any inconvenience. Thank you for your patience.
        </p>
      </div>
    </div>
  );
};

export default MaintenancePage;
